import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { teamMember } from "../apiPath/api";

export const createteamMember = createAsyncThunk(
  "teamMember/createteamMember",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await teamMember.createTeamMember(formData);
      return response.data;
    } catch (error) {
      // Extract complete error message
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to create team member";

      // If there are validation errors, include them
      const validationErrors = error.response?.data?.errors || {};
      const errorDetails = Object.values(validationErrors).join(", ");

      const fullMessage = errorDetails
        ? `${message}. ${errorDetails}`
        : message;

      return rejectWithValue(fullMessage);
    }
  }
);

export const getAllTeamMembers = createAsyncThunk(
  "teamMember/getAllTeamMembers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await teamMember.getAllTeamMembers(params);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to fetch team members";
      return rejectWithValue(message);
    }
  }
);

export const deleteTeamMembers = createAsyncThunk(
  "teamMember/deleteTeamMembers",
  async (id, { rejectWithValue }) => {
    try {
      const response = await teamMember.deleteTeamMembers(id);
      return response.data.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to delete team member";
      return rejectWithValue(message);
    }
  }
);

export const undoDeleteTeamMembers = createAsyncThunk(
  "teamMember/undoDeleteTeamMembers",
  async (id, { rejectWithValue }) => {
    try {
      const response = await teamMember.undoDeleteTeamMembers(id);
      return response.data.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to restore team member";
      return rejectWithValue(message);
    }
  }
);

export const updateTeamMembers = createAsyncThunk(
  "teamMember/updateTeamMembers",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await teamMember.updateTeamMembers(id, data);
      return response.data;
    } catch (error) {
      console.error("Update error:", error);

      // Extract complete error message
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to update team member";

      // If there are validation errors, include them
      const validationErrors = error.response?.data?.errors || {};
      const errorDetails = Object.values(validationErrors).join(", ");

      const fullMessage = errorDetails
        ? `${message}. ${errorDetails}`
        : message;

      return rejectWithValue(fullMessage);
    }
  }
);

export const updateTeamMemberOrder = createAsyncThunk(
  "teamMember/updateOrder",
  async (newOrder, { rejectWithValue }) => {
    try {
      const response = await teamMember.updateTeamMemberOrder(newOrder);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to update team member order";
      return rejectWithValue(message);
    }
  }
);

const teamMemberSlice = createSlice({
  name: "teamMember",
  initialState: {
    loading: false,
    error: null,
    pagination: { total: 0, page: 1, limit: 10, totalPages: 1 },
    statusCounts: { all: 0, active: 0, inactive: 0, deleted: 0 },
    search: "",
    status: "all",
    limit: 10,
    page: 1,
    success: false,
    teamMember: [],
  },

  reducers: {
    resetTeamMemberState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.teamMember = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createteamMember.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createteamMember.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.createdField = action.payload;
      })
      .addCase(createteamMember.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      .addCase(getAllTeamMembers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllTeamMembers.fulfilled, (state, action) => {
        state.loading = false;
        state.teamMember = action.payload.data;
        state.pagination = action.payload.pagination;
        state.statusCounts = action.payload.statusCounts;
      })
      .addCase(getAllTeamMembers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteTeamMembers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTeamMembers.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteTeamMembers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(undoDeleteTeamMembers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(undoDeleteTeamMembers.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(undoDeleteTeamMembers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateTeamMembers.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateTeamMembers.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.updatedField = action.payload;
      })
      .addCase(updateTeamMembers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
  },
});

export const { resetTeamMemberState } = teamMemberSlice.actions;
export default teamMemberSlice.reducer;
